import React, { useEffect, useRef, useState } from "react";
import { FaCheck, FaPlus } from 'react-icons/fa';
import { useQueries } from "react-query";
import ReactLoading from "react-loading";
import * as helpers from "../../utils/utils.js";
import "./slotsForDate.css";
import { useSlots } from "../../hooks/useSlots.js";
import { usePrices } from "../../hooks/usePrices.js";
import { useUnits } from "../../hooks/useUnits.js";

// tile that shows unit information
function UnitTile({ unit }) {
  return (
    <div className="unit-tile-content">
      <div className="unit-tile-image">
        <img src={unit.picture} alt={unit.name} />
      </div>
      <div className="unit-tile-text">
        <p className="unit-tile-name">{unit.name}</p>
        <p className="unit-tile-info">Engine: {unit.hp}hp</p>
        <p className="unit-tile-info">Max. Persons: {unit.capacity}</p>
      </div>
    </div>
  );
}

function SlotsForDate({
  selectedDates, availableSlots, selectedSlots, 
  addSlot, removeSlot, removeDate, sectionRef
}) {
  const { retrievePriceById } = usePrices();
  const { retrieveUnitById } = useUnits();
  const [selectedOptions, setSelectedOptions] = useState({}); 

  const visibleSlots = availableSlots.filter(slot =>
    selectedDates.includes(slot.date)
  );

  const priceIdsToFetch = Array.from(
    new Set(visibleSlots.map(slot => slot.priceId))
  );

  const unitIdsToFetch = Array.from(
    new Set(visibleSlots.map(slot => slot.unitId))
  );
    
  const priceQueries = useQueries(
    priceIdsToFetch.map(priceId => ({
      queryKey: ['price', priceId],
      queryFn: () => retrievePriceById(priceId),
      staleTime: 1000 * 60 * 30, // optional: cache for 5 minutes{
      refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
      cacheTime: 1000 * 60 * 60,          // 1 hour cache
    }))
  );
  
  const unitQueries = useQueries(
    unitIdsToFetch.map(unitId => ({
      queryKey: ['unit', unitId],
      queryFn: () => retrieveUnitById(unitId),
      staleTime: 1000 * 60 * 30, // optional: cache for 5 minutes{
      refetchOnWindowFocus: false,        // prevent re-fetch on tab switch
      cacheTime: 1000 * 60 * 60,  
    }))
  );

  const priceMap = {};
  priceQueries.forEach((query, index) => {
    if (query.isSuccess) {
      priceMap[priceIdsToFetch[index]] = query.data;
    }
  });

  const unitMap = {};
  unitQueries.forEach((query, index) => {
    if (query.isSuccess) {
      unitMap[unitIdsToFetch[index]] = query.data;
    }
  });

  const isPriceLoading = priceQueries.some(q => q.isLoading || q.isFetching);
  const isUnitLoading = unitQueries.some(q => q.isLoading || q.isFetching);

  const bookableSlots = visibleSlots.filter((slot) => {
    const price = priceMap[slot.priceId];
    return price && !helpers.hasSlotEndTimePassed(slot, price);
  });


  const handleOptionChange = (date, unitId, slotId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [`${date}-${unitId}`]: slotId, // Store priceId for specific unitId and date
    }));
  };

  const handleUnitSelectionChange = (unitId, date) => {
    const isSelected = selectedSlots.some(
      (slot) => slot.unitId === unitId && slot.date === date,
    );

    if (isSelected) {
      removeSlot({ unitId, date });
    } else {
      const unitDateString = `${date}-${unitId}`;
      const newSlotId = selectedOptions[unitDateString];
      const newSlot = availableSlots.find(slot => slot.id === newSlotId);
      const price = priceMap[newSlot?.priceId];

      if (!newSlot || !price || helpers.hasSlotEndTimePassed(newSlot, price)) {
        return;
      }

      addSlot(newSlot);
    }
  };

  return (
    <div className="slots-for-date" ref={sectionRef}>
      {/* <div className="selectable-units main-border">
        <p className="section-heading">
          {`Selections (${String(selectedSlots?.length) ?? "0"})`}
        </p>
        <div className="unit-selection-instructions"></div> */}
        {(isPriceLoading || isUnitLoading) ? (
          <div className="flex-center">
            <ReactLoading type="spin" color="#6c757d" height={60} width={60} />
          </div>
        ) : (
        <div className="selected-dates">
        
        {selectedDates.map((selectedDate) => {
          
          const formattedDate = new Date(
            parseInt(selectedDate),
          ).toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            timeZone: "UTC",
          });

          const unitsForDate = Array.from(
            new Set(bookableSlots
              .filter(slot => slot.date === selectedDate)
              .map(slot => (slot.unitId))
            )
          ).sort();
          
          return (
            <div className="date-container main-border" key={selectedDate}>
              <div className="date-header">
                <div className="date-options">{formattedDate}</div>

                <div className="btn-container">
                  <button
                    className="btn remove-btn"
                    onClick={() => removeDate(selectedDate)}
                  >
                    X
                  </button>
                </div>
              </div>
              <div className="tile-container">
                {unitsForDate.length === 0 ? (
                  <p className="no-slots-message">
                    All online booking slots for this date have already ended.
                  </p>
                ) : unitsForDate.map((unitId) => {

                  const unit = unitMap[unitId];

                  const openSlots = bookableSlots
                    .filter(slot => slot.date === selectedDate && slot.unitId === unitId)
                    .sort((a, b) => b.priceId - a.priceId);

                  if (openSlots.length === 0) return null;
                  const unitDateString = `${selectedDate}-${unitId}`;
                  if (!openSlots.some((slot) => slot.id === selectedOptions[unitDateString])) {
                    setSelectedOptions((prevState) => {
                      const updatedOptions = {...prevState};
                      updatedOptions[unitDateString] = openSlots[0].id;
                      return updatedOptions;
                    })
                  }

                  const isSelected = selectedSlots?.some(
                    (slot) =>
                      slot?.date === selectedDate && slot?.unitId === unitId,
                  );


                  return (
                    <div
                      key={`${selectedDate}-${unitId}`}
                      className={`unit-tile-container ${isSelected ? "selected" : ""}`}
                      onClick={() => handleUnitSelectionChange(unitId, selectedDate)}
                    >

                      {isSelected ? <div className="selected-label"><FaCheck /> Selected</div> 
                      :
                      <div className="selected-label"><FaPlus /> Select</div>}

                      <UnitTile
                        className="selectable-unit-tile"
                        unit={ unit }
                      ></UnitTile>

                      {openSlots.length === 1 ? (
                        <div className="slot-single-option">
                          {priceMap[openSlots[0].priceId].name} ${priceMap[openSlots[0].priceId].price}
                        </div>
                      ) : (
                        <select
                          className="slot-dropdown"
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleOptionChange(selectedDate, unitId, e.target.value)}
                          value={selectedOptions[`${selectedDate}-${unitId}`]}
                        >
                          {openSlots.map(
                            (slot) => {
                              return (
                                <option
                                  value={slot.id}
                                  key={`${selectedDate}-${unitId}-${slot.id}`}
                                >
                                  {priceMap[slot.priceId].name} $
                                  {priceMap[slot.priceId].price}
                                </option>
                              );
                            },
                          )}
                        </select>
                      )}


                      {/* <div
                        className="slot-list"
                      >
                        {selectedDateUnits[selectedDate][unitId].map(
                          (priceId, index) => {
                            return (
                              <div className={`slot-list-item ${selectedOptions[`${selectedDate}-${unitId}`] === priceId && "selected"}`}
                                onClick={(e) => {
                                  e.stopPropagation(); 
                                  handleOptionChange(selectedDate, unitId, priceId);
                                }}
                                key={index}
                              >
                                {prices[priceId]?.name} <br />$
                                {prices[priceId].price}
                              </div>
                            );
                          },
                        )}
                      </div> */}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        </div>
        )}
      {/* </div> */}
    </div>
  );
}

export default SlotsForDate;
